/**
 * App 上报消息「附带坐标打点」：持久地图点，仅手动删除消失
 */
import mapboxgl from 'mapbox-gl';
import { toMapLngLat, getActiveBasemapIdForCoord } from './basemapCoord';
import apiFetch from '../apiFetch';
import { copyToClipboard } from './clipboardHelpers';

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function readProjectionName(map) {
  try {
    return map?.getProjection?.()?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}
function formatCoordNum(value, digits = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(digits);
}

function formatAltitude(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? `${n}` : n.toFixed(1);
}

/** 生成复制用坐标文本：仅纬度, 经度（不含海拔） */
function buildCopyCoordText(pin) {
  const lat = formatCoordNum(pin.latitude);
  const lng = formatCoordNum(pin.longitude);
  if (lat == null || lng == null) return '';
  return `${lat}, ${lng}`;
}

/**
 * 上报打点图标：浅色水滴针 + 上方消息气泡
 * 与队员 App 定位针（实心色 + App 角标）刻意区分
 */
function createPinElement(pin) {
  const important = !!pin.important;
  const el = document.createElement('div');
  el.className = 'zhxt-field-msg-pin';
  el.title = `${pin.name || '队员'} · 上报打点`;
  el.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'cursor:pointer',
    'pointer-events:auto',
    'max-width:170px',
    'line-height:0'
  ].join(';');

  const label = document.createElement('div');
  label.className = 'zhxt-field-msg-pin-label';
  label.style.cssText = [
    'padding:5px 7px',
    'margin-bottom:3px',
    important ? 'background:rgba(254,242,242,.92)' : 'background:rgba(255,248,225,.92)',
    important ? 'border:1px solid #f5b5b5' : 'border:1px solid #e8c98a',
    'border-radius:4px',
    'box-shadow:0 1px 6px rgba(0,0,0,.14)',
    'font-size:11px',
    'line-height:1.35',
    'color:#444',
    'white-space:normal',
    'word-break:break-word',
    'text-align:left'
  ].join(';');
  const name = escapeHtml(pin.name || '队员');
  const text = escapeHtml(String(pin.text || '').slice(0, 80));
  const titleColor = important ? '#c45c5c' : '#c4a06a';
  label.innerHTML = `<div style="font-weight:700;color:${titleColor};margin-bottom:2px">${name}</div>`
    + `<div>${text}</div>`;

  // 浅色半透明针：比队员图标更淡；角标用「报」区分 App
  const pinFill = important ? '#f0b4b4' : '#f0d9a8';
  const pinStroke = important ? '#e8a0a0' : '#e0c48a';
  const badgeFill = important ? '#d97777' : '#c4a574';
  const icon = document.createElement('div');
  icon.style.cssText = 'width:30px;height:36px;margin:0;padding:0;line-height:0;opacity:0.88';
  icon.innerHTML = `
    <svg width="30" height="36" viewBox="0 0 30 36" xmlns="http://www.w3.org/2000/svg" style="display:block;pointer-events:none">
      <g transform="translate(2,3)">
        <path d="M13 1C6.9 1 2 5.9 2 12c0 8.5 11 19 11 19s11-10.5 11-19C24 5.9 19.1 1 13 1z"
          fill="${pinFill}" stroke="${pinStroke}" stroke-width="1" fill-opacity="0.72"/>
        <circle cx="13" cy="12" r="4" fill="#fffef8" fill-opacity="0.9"/>
      </g>
      <rect x="15" y="1" width="14" height="10" rx="2" fill="${badgeFill}" fill-opacity="0.85"/>
      <text x="22" y="8.5" text-anchor="middle" fill="#fff" font-size="7.5" font-weight="700"
        font-family="system-ui,sans-serif">报</text>
    </svg>
  `;

  el.appendChild(label);
  el.appendChild(icon);
  return el;
}

function buildPinPopupHtml(pin) {
  const when = pin.created_at ? new Date(pin.created_at).toLocaleString() : '';
  const lat = formatCoordNum(pin.latitude);
  const lng = formatCoordNum(pin.longitude);
  const alt = formatAltitude(pin.altitude ?? pin.alt);
  const copyText = buildCopyCoordText(pin);
  return `
    <div style="width:max-content;max-width:200px;font-size:11px;line-height:1.35">
      <div style="font-weight:700;margin-bottom:2px">${escapeHtml(pin.name || '队员')} · 消息打点</div>
      <div style="white-space:pre-wrap;word-break:break-word;margin-bottom:3px">${escapeHtml(pin.text || '')}</div>
      <div style="color:#334155;margin-bottom:2px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:10px;line-height:1.3">
        <div>纬度：${lat != null ? escapeHtml(lat) : '—'}</div>
        <div>经度：${lng != null ? escapeHtml(lng) : '—'}</div>
        <div>海拔：${alt != null ? `${escapeHtml(alt)} m` : '—'}</div>
      </div>
      <div style="color:#64748b;margin-bottom:4px;font-size:10px">${escapeHtml(when)}</div>
      <div style="display:flex;gap:4px;justify-content:flex-end">
        <button type="button" data-zhxt-pin-copy="${escapeHtml(copyText)}"
          style="padding:1px 6px;font-size:10px;line-height:1.3;border:1px solid #1976d2;background:#fff;color:#1565c0;border-radius:3px;cursor:pointer;white-space:nowrap">
          复制坐标
        </button>
        <button type="button" data-zhxt-pin-delete="${escapeHtml(pin.id)}"
          style="padding:1px 6px;font-size:10px;line-height:1.3;border:1px solid #ef4444;background:#fff;color:#b91c1c;border-radius:3px;cursor:pointer;white-space:nowrap">
          删除点位
        </button>
      </div>
    </div>
  `;
}

/**
 * @param {Map} pinsMap Map<id, { marker, pin }>
 */
export function upsertFieldMessagePinOnMap(map, pin, pinsMap, options = {}) {
  if (!map || !pin?.id || !pinsMap) return null;
const projection = readProjectionName(map);
  if (projection === 'globe') {
    return null;
  }

  const lng = Number(pin.longitude);
  const lat = Number(pin.latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const existing = pinsMap.get(pin.id);
  if (existing?.marker) {
    try { existing.marker.remove(); } catch (_) { /* ignore */ }
    pinsMap.delete(pin.id);
  }

  const display = toMapLngLat(lng, lat, getActiveBasemapIdForCoord(map), map) || [lng, lat];
  const el = createPinElement(pin);
  const popup = new mapboxgl.Popup({
    offset: 24,
    maxWidth: '220px',
    closeButton: true,
    // 保证弹层盖住同点位的坐标标签
    className: 'zhxt-field-msg-popup',
    anchor: 'bottom'
  }).setHTML(buildPinPopupHtml(pin));

  // 针尖对准坐标；气泡在针上方，略偏开队员实心定位针
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport',
    offset: [14, 0]
  })
    .setLngLat(display)
    .setPopup(popup)
    .addTo(map);

  const onPopupClick = async (event) => {
    const copyBtn = event.target?.closest?.('[data-zhxt-pin-copy]');
    if (copyBtn) {
      event.preventDefault();
      event.stopPropagation();
      const text = copyBtn.getAttribute('data-zhxt-pin-copy') || buildCopyCoordText(pin);
      if (!text) return;
      const original = copyBtn.textContent;
      copyToClipboard(text, {
        successMessage: '',
        failMessage: '复制失败，请手动复制坐标'
      });
      copyBtn.textContent = '已复制';
      setTimeout(() => {
        if (copyBtn.isConnected) copyBtn.textContent = original || '复制坐标';
      }, 1200);
      return;
    }

    const btn = event.target?.closest?.('[data-zhxt-pin-delete]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const id = btn.getAttribute('data-zhxt-pin-delete');
    if (!id) return;
    btn.disabled = true;
    btn.textContent = '删除中…';
    try {
      // 优先 POST（兼容部分网关禁用 DELETE），失败再试 DELETE
      let resp = await apiFetch('/api/tracker/message-pins/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      let raw = await resp.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch (_) { /* ignore */ }

      if (!resp.ok || !data.success) {
        resp = await apiFetch(`/api/tracker/message-pins/${encodeURIComponent(id)}`, {
          method: 'DELETE'
        });
        raw = await resp.text();
        try { data = raw ? JSON.parse(raw) : {}; } catch (_) { data = {}; }
      }

      // 无论服务端是否返回旧版「打点表不可用」，本地点已可删；仅非 2xx 且无 success 才算失败
      if (resp.ok && data.success !== false) {
        removeFieldMessagePinFromMap(pinsMap, id);
        options.onDeleted?.(id, { success: true, ...data, warning: undefined });
        return;
      }
      if (!resp.ok && /打点表/.test(String(data.error || ''))) {
        removeFieldMessagePinFromMap(pinsMap, id);
        options.onDeleted?.(id, { success: true });
        return;
      }
      throw new Error(data.error || `删除失败 HTTP ${resp.status}`);
    } catch (error) {
      // 网络异常时仍移除本地气泡，避免卡死；不弹「打点表」类提示
      removeFieldMessagePinFromMap(pinsMap, id);
      const softMsg = String(error?.message || '');
      if (/打点表/.test(softMsg)) {
        options.onDeleted?.(id, { success: true });
      } else {
        options.onDeleteError?.(error, id);
      }
    }
  };

  const onPopupOpen = () => {
    el.classList.add('is-popup-open');
    // 打开时压低本点 marker，抬高弹层，避免标签挡住操作按钮
    if (el.parentElement) el.parentElement.style.zIndex = '1';
    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.zIndex = '40';
      popupEl.addEventListener('click', onPopupClick);
    }
  };
  const onPopupClose = () => {
    el.classList.remove('is-popup-open');
    if (el.parentElement) el.parentElement.style.zIndex = '';
    const popupEl = popup.getElement();
    popupEl?.removeEventListener('click', onPopupClick);
  };
  popup.on('open', onPopupOpen);
  popup.on('close', onPopupClose);

  pinsMap.set(pin.id, { marker, pin, popup });
  return pinsMap.get(pin.id);
}

export function removeFieldMessagePinFromMap(pinsMap, id) {
  if (!pinsMap || !id) return;
  const item = pinsMap.get(id);
  if (!item) return;
  try { item.marker?.remove(); } catch (_) { /* ignore */ }
  pinsMap.delete(id);
}

export function reprojectFieldMessagePins(map, pinsMap, basemapId) {
  if (!map || !pinsMap) return;
  pinsMap.forEach((item) => {
    const pin = item?.pin;
    if (!pin || !item.marker) return;
    const display = toMapLngLat(
      Number(pin.longitude),
      Number(pin.latitude),
      basemapId || getActiveBasemapIdForCoord(map),
      map
    );
    if (display) item.marker.setLngLat(display);
  });
}

export function clearAllFieldMessagePins(pinsMap) {
  if (!pinsMap) return;
  pinsMap.forEach((item) => {
    try { item.marker?.remove(); } catch (_) { /* ignore */ }
  });
  pinsMap.clear();
}

export async function fetchActiveFieldMessagePins(fetchFn = apiFetch) {
  const response = await fetchFn('/api/tracker/message-pins');
  const raw = await response.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch (_) {
    throw new Error('获取消息打点失败：返回非 JSON（请确认后端已重启）');
  }
  if (!response.ok || !data.success) {
    throw new Error(data.error || '获取消息打点失败');
  }
  return data.pins || [];
}
